import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Quote, ArrowUpRight, Star } from "lucide-react";
import SectionHeader from "../components/SectionHeader";
import { api } from "../lib/api";
import { mediaUrl } from "../lib/siteMedia";

const HERO_SLIDES = [
  {
    mkey: "hero_1",
    eyebrow: "An Invitation",
    title: "Where Success Meets Tradition.",
    sub: "A private members club, restaurant, and cigar lounge in the heart of Lake Nona.",
  },
  {
    mkey: "hero_2",
    eyebrow: "The Founders 100",
    title: "One hundred seats. A lifetime of recognition.",
    sub: "Founding membership applications are now being reviewed in waves of twenty.",
  },
  {
    mkey: "hero_3",
    eyebrow: "Smoke. Sip. Win.",
    title: "An evening that does not end at the front door.",
    sub: "Rare whiskey, hand-selected cigars, and the people who shape the city.",
  },
];

function HeroCarousel({ media }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_SLIDES.length), 6500);
    return () => clearInterval(t);
  }, []);
  return (
    <section data-testid="home-hero" className="relative w-full min-h-[100svh] overflow-hidden">
      {HERO_SLIDES.map((s, i) => (
        <div
          key={i}
          className={`carousel-slide ${i === idx ? "active" : ""}`}
          style={{ backgroundImage: `url(${mediaUrl(media, s.mkey)})` }}
          aria-hidden={i !== idx}
        >
          <div className="absolute inset-0 ken-burns" style={{ backgroundImage: `url(${mediaUrl(media, s.mkey)})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        </div>
      ))}
      <div className="absolute inset-0 hero-overlay" />
      <div className="relative z-10 max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 min-h-[100svh] flex flex-col justify-end pb-24 sm:pb-28 pt-32">
        <div className="max-w-3xl">
          <p className="eyebrow fade-in" data-testid="hero-eyebrow">{HERO_SLIDES[idx].eyebrow}</p>
          <h1 className="font-serif text-[40px] sm:text-6xl lg:text-7xl xl:text-[88px] leading-[1.02] tracking-tight mt-6 fade-in" key={`t-${idx}`} data-testid="hero-title">
            {HERO_SLIDES[idx].title}
          </h1>
          <p className="text-[var(--twr-warm-white)]/80 text-base sm:text-lg mt-6 max-w-xl fade-in" key={`s-${idx}`}>
            {HERO_SLIDES[idx].sub}
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/membership" data-testid="hero-cta-membership" className="btn-bronze">Become a Member <ChevronRight size={14} strokeWidth={2} /></Link>
            <Link to="/reservations" data-testid="hero-cta-reserve" className="btn-ghost">Reserve a Table</Link>
          </div>
        </div>

        <div className="flex items-end justify-between mt-16 gap-6 flex-wrap">
          <div className="flex items-center gap-2">
            {HERO_SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`Slide ${i + 1}`}
                data-testid={`hero-dot-${i}`}
                className={`h-[2px] transition-all duration-500 ${i === idx ? "w-12 bg-[var(--twr-bronze)]" : "w-6 bg-white/25 hover:bg-white/50"}`}
              />
            ))}
          </div>
          <div className="hidden sm:flex items-center gap-3 text-[11px] tracking-[0.32em] uppercase text-[var(--twr-warm-white)]/55">
            <span>Lake Nona</span>
            <span className="w-6 h-px bg-[var(--twr-line-strong)]" />
            <span>Orlando, Florida</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function Marquee() {
  const items = [
    "Founders 100", "Private Boardrooms", "Walk-In Humidor", "Chef-Driven Kitchen",
    "Rare Whiskey Library", "Podcast Studio", "Member Lockers", "Live Sessions"
  ];
  return (
    <div className="border-y border-[var(--twr-line)] overflow-hidden py-6 bg-[#0c0b0a]">
      <div className="marquee-track">
        {[...items, ...items, ...items].map((w, i) => (
          <span key={i} className="marquee-item">
            <span className="dot" /> {w}
          </span>
        ))}
      </div>
    </div>
  );
}

function About({ media }) {
  return (
    <section className="section relative" data-testid="home-about">
      <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <div className="frame aspect-[4/5] w-full">
            <img src={mediaUrl(media, "about")} alt="Leather armchair in the lounge" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="lg:col-span-7 order-1 lg:order-2">
          <SectionHeader eyebrow="The Win Room" title={<>A private club for those building their legacy.</>} />
          <p className="text-[var(--twr-warm-white)]/75 text-base leading-relaxed mt-8 max-w-2xl">
            Set within Lake Nona's quiet centre of gravity, The Win Room is a sanctuary for founders, executives, and the
            quietly accomplished. A restaurant by night. A boardroom by day. A cigar lounge in the in-between — built around
            the unwritten rule that good business has always travelled with good company.
          </p>
          <p className="text-[var(--twr-warm-white)]/70 text-base leading-relaxed mt-5 max-w-2xl">
            Walnut, brass, marble, and leather. A walk-in humidor stocked from Geneva to Estelí. A wine list curated for
            people who don't read wine lists. And one hundred Founding seats — each carrying lifetime recognition on our
            permanent wall.
          </p>
          <div className="grid grid-cols-3 gap-6 sm:gap-10 mt-12 max-w-lg">
            <div className="stat"><span className="stat-value">100</span><span className="stat-label">Founders</span></div>
            <div className="stat"><span className="stat-value">12k</span><span className="stat-label">Sq. ft.</span></div>
            <div className="stat"><span className="stat-value">14</span><span className="stat-label">Private Lockers</span></div>
          </div>
          <Link to="/about" className="link-bronze mt-12 inline-flex items-center gap-2 text-sm uppercase tracking-[0.28em]">
            Read the story <ArrowUpRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function PillarCard({ to, eyebrow, title, copy, img, testId }) {
  return (
    <Link to={to} data-testid={testId} className="group block surface relative overflow-hidden">
      <div className="aspect-[4/3] overflow-hidden">
        <img src={img} alt={title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
      </div>
      <div className="p-7 sm:p-9">
        <p className="eyebrow">{eyebrow}</p>
        <h3 className="font-serif text-2xl sm:text-3xl mt-3 leading-tight">{title}</h3>
        <p className="text-[var(--twr-warm-white)]/65 text-sm mt-4 leading-relaxed">{copy}</p>
        <span className="link-bronze mt-7 inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em]">
          Discover <ArrowUpRight size={13} strokeWidth={1.5} />
        </span>
      </div>
    </Link>
  );
}

function Pillars({ media }) {
  const pillars = [
    { to: "/restaurant", eyebrow: "01 — Kitchen", title: "A chef-driven kitchen.", copy: "Wood-fired prime cuts, raw bar, and a tasting menu shaped by Florida's coast and farms.", img: mediaUrl(media, "pillar_restaurant"), testId: "home-pillar-restaurant" },
    { to: "/bar", eyebrow: "02 — Bar", title: "Rare whiskey. Quiet rooms.", copy: "A private bar library with two hundred labels — from Yamazaki to Pappy Van Winkle.", img: mediaUrl(media, "pillar_bar"), testId: "home-pillar-bar" },
    { to: "/cigar-lounge", eyebrow: "03 — Lounge", title: "A walk-in humidor.", copy: "Hand-selected from Davidoff, Padron, Fuente, and producers known only to those who know.", img: mediaUrl(media, "pillar_cigar"), testId: "home-pillar-cigar" },
    { to: "/private-spaces", eyebrow: "04 — Boardroom", title: "Spaces that mean business.", copy: "Conference room, podcast studio, and a VIP lounge — bookable by members.", img: mediaUrl(media, "pillar_private"), testId: "home-pillar-private" },
  ];
  return (
    <section className="section bg-[#0c0b0a]" data-testid="home-pillars">
      <div className="max-w-[1600px] mx-auto">
        <SectionHeader eyebrow="The Four Rooms" title="Four rooms. One address." kicker="Every detail considered. Every room with intent." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
          {pillars.map((p) => (<PillarCard key={p.to} {...p} />))}
        </div>
      </div>
    </section>
  );
}

function Founders({ media }) {
  return (
    <section className="relative section overflow-hidden" data-testid="home-founders">
      <div className="absolute inset-0">
        <img src={mediaUrl(media, "founders_bg")} alt="" className="w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-[#0A0A0A]/80" />
      </div>
      <div className="relative max-w-[1300px] mx-auto text-center">
        <span className="tier-badge mx-auto">The Founders 100</span>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-7xl mt-8 leading-[1.05] tracking-tight">
          One hundred names. <span className="italic" style={{ color: "var(--twr-bronze)" }}>One permanent wall.</span>
        </h2>
        <p className="text-[var(--twr-warm-white)]/75 text-lg mt-8 max-w-2xl mx-auto leading-relaxed">
          Founding membership is offered to one hundred individuals — each recognised, in perpetuity, on the Founders Wall
          at the entrance of the club. Locked initiation. Lifetime privileges.
        </p>
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl mx-auto">
          {[
            { v: "100", l: "Total Seats" },
            { v: "38", l: "Inducted" },
            { v: "62", l: "Remaining" },
            { v: "∞", l: "Recognition" },
          ].map((s) => (
            <div key={s.l} className="surface-elev p-5"><div className="stat-value">{s.v}</div><div className="stat-label mt-2">{s.l}</div></div>
          ))}
        </div>
        <Link to="/membership/apply" data-testid="founders-cta" className="btn-bronze mt-12 inline-flex">Submit Application <ChevronRight size={14} /></Link>
      </div>
    </section>
  );
}

function Events({ events }) {
  return (
    <section className="section" data-testid="home-events">
      <div className="max-w-[1600px] mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <SectionHeader eyebrow="Calendar" title={<>Upcoming evenings <br className="hidden lg:block"/>at the club.</>} kicker="From single-malt flights to fireside conversations with founders." />
          <Link to="/events" className="link-bronze inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em]">View All Events <ArrowUpRight size={14} /></Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
          {events.slice(0, 6).map((e) => (
            <Link key={e.id} to="/events" data-testid={`home-event-${e.id}`} className="group product-card overflow-hidden">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={e.image} alt={e.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-6">
                <p className="eyebrow">{new Date(e.date).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" })} · {e.category.replace(/-/g, " ")}</p>
                <h3 className="font-serif text-xl sm:text-2xl mt-3 leading-snug">{e.title}</h3>
                <p className="text-[var(--twr-warm-white)]/65 text-sm mt-3 line-clamp-2">{e.description}</p>
                <div className="flex items-center justify-between mt-5 text-[12px] tracking-widest uppercase text-[var(--twr-bronze)]">
                  <span>{e.members_only ? "Members Only" : "Open Invitation"}</span>
                  <span>{e.price > 0 ? `$${e.price.toFixed(0)}` : "Complimentary"}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials({ items }) {
  return (
    <section className="section bg-[#0c0b0a]" data-testid="home-testimonials">
      <div className="max-w-[1300px] mx-auto">
        <SectionHeader eyebrow="In Their Words" title="Said by those who built it." align="center" />
        <div className="grid md:grid-cols-2 gap-6 mt-16">
          {items.map((t) => (
            <figure key={t.id} className="surface p-8 sm:p-10">
              <Quote className="text-[var(--twr-bronze)]" size={28} strokeWidth={1.2} />
              <blockquote className="font-editorial italic text-xl sm:text-2xl leading-snug mt-5 text-[var(--twr-warm-white)]/90">
                "{t.quote}"
              </blockquote>
              <figcaption className="mt-8 flex items-center justify-between">
                <div>
                  <p className="font-serif text-base">{t.name}</p>
                  <p className="text-[11px] tracking-[0.22em] uppercase text-[var(--twr-warm-white)]/55 mt-1">{t.title}</p>
                </div>
                <div className="flex gap-0.5">
                  {[...Array(t.rating || 5)].map((_, i) => (<Star key={i} size={12} className="fill-[var(--twr-bronze)] text-[var(--twr-bronze)]" />))}
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function Instagram({ media }) {
  const keys = ["insta_1", "insta_2", "insta_3", "insta_4", "insta_5", "insta_6"];
  const IG_URL = "https://www.instagram.com/thewinroomusa/";
  return (
    <section className="section-tight" data-testid="home-instagram">
      <div className="max-w-[1600px] mx-auto">
        <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
          <div>
            <p className="eyebrow">Instagram</p>
            <h3 className="font-serif text-3xl sm:text-4xl mt-3">@thewinroomusa</h3>
          </div>
          <a href={IG_URL} target="_blank" rel="noopener noreferrer" className="link-bronze text-xs uppercase tracking-[0.28em]">Follow Us</a>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {keys.map((k) => (
            <a key={k} href={IG_URL} target="_blank" rel="noopener noreferrer" className="block aspect-square overflow-hidden group">
              <img src={mediaUrl(media, k)} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [events, setEvents] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [media, setMedia] = useState({});
  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/site-media").then((r) => setMedia(r.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="home-page">
      <HeroCarousel media={media} />
      <Marquee />
      <About media={media} />
      <Pillars media={media} />
      <Founders media={media} />
      <Events events={events} />
      <Testimonials items={testimonials} />
      <Instagram media={media} />
    </div>
  );
}
